/**
 * Dialog Component
 *
 * A reusable, responsive confirmation and alert dialog component for Angular applications.
 * Suitable for critical actions like deletions, confirmations, and warnings.
 *
 * ### Features:
 * - Configurable title, message, buttons, icon, and size via `DialogConfig`
 * - Emits `(closed)` event with a boolean indicating confirmation (`true`) or cancellation (`false`)
 * - Automatically adjusts button layout for mobile devices (<576px width)
 * - Fully responsive using a mobile-first design approach
 *
 * ### Inputs:
 * - `[config]` (`DialogConfig`): Object containing all customization options
 *
 * ### Outputs:
 * - `(closed)` (`EventEmitter<boolean>`): Fired when the dialog is closed, with:
 *   - `true` → user confirmed
 *   - `false` → user canceled
 *
 * ### Usage Example:
 * ```html
 * <app-dialog
 *   [config]="{
 *     title: 'Deactivate account',
 *     message: 'Are you sure you want to deactivate your account? All of your data will be permanently removed.',
 *     confirmText: 'Deactivate',
 *     cancelText: 'Cancel',
 *     confirmBtnClass: 'btn-danger',
 *     icon: 'bi bi-exclamation-triangle-fill',
 *     iconColor: '#dc3545',
 *     layout: 'side-by-side',
 *     size: 'sm'
 *   }"
 *   (closed)="onDialogClosed($event)"
 * ></app-dialog>
 * ```
 *
 * ### Notes:
 * - Can be integrated with a DialogService to be opened programmatically
 * - Designed to replace native browser confirm dialogs with a styled alternative
 */
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';

/**
 * Defines all customizable properties of the dialog.
 */
export interface DialogConfig {
  /** The dialog title (optional). */
  title?: string;

  /** The main message text to display (required). */
  message: string;

  /** Label for the confirm/primary button (default: "OK"). */
  confirmText?: string;

  /** Label for the cancel/secondary button (default: "Cancel"). */
  cancelText?: string;

  /** CSS classes to apply to the confirm button (e.g., 'btn-danger'). */
  confirmBtnClass?: string;

  /** Icon class name (e.g., Font Awesome, Bootstrap Icons). */
  icon?: string;

  /** CSS color value for the icon (e.g., '#dc3545'). */
  iconColor?: string;

  /** Button arrangement layout: 'side-by-side' (default) or 'stacked'. */
  layout?: 'side-by-side' | 'stacked';

  /** Optional dialog size modifier: 'sm' (small) or 'lg' (large). */
  size?: 'sm' | 'lg';
}
// Declare the Angular component
@Component({
  selector: 'app-dialog',
  imports: [CommonModule],
  templateUrl: './dialog.html',
  styleUrl: './dialog.scss'
})
export class Dialog {
  /**
   * Input: Configuration object controlling dialog content, appearance, and behavior.
   */
  @Input() config!: DialogConfig;
  /**
   * Output: Emits when the dialog is closed.
   * The emitted value is `true` if confirmed, `false` if canceled.
   */
  @Output() closed = new EventEmitter<boolean>();
  /**
   * Internal state: Tracks whether the current screen size is mobile (<576px).
   * Used to adjust layout dynamically.
   */
  isMobile: boolean = false;
  /**
   * Variable which is used to hold all subscriptions for cleanup on component destroy.
   */
  private readonly subscriptions: Subscription = new Subscription();
  /**
   * Computed property: Returns the Bootstrap modal size class
   * based on the `config.size` setting.
   */
  get sizeClass() {
    return this.config.size ? `modal-${this.config.size}` : '';
  }
  /**
   * Component constructor which is used to inject the required services.
   * @param router To manage navigation.
   */
  constructor(private router: Router) { }
  /**
   * Lifecycle hook that is called once after component initialization.
   * Checks the initial screen size to set the `isMobile` flag.
   */
  public ngOnInit(): void {
    this.checkScreenSize();
    // Close dialog on route change (Back / Forward / Navigation)
    this.subscriptions.add(this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.close(false);
      }
    }));
  }
  /**
   * Closes the dialog and emits the result.
   * @param value - `true` if the user confirmed, `false` if canceled.
   */
  public close(value: boolean): void {
    this.closed.emit(value);
  }
  /**
   * HostListener: Listens to window resize events.
   * Runs `checkScreenSize()` to adapt layout for current screen width.
   */
  @HostListener('window:resize', [])
  onResize() {
    this.checkScreenSize();
  }
  /**
   * Determines if the current screen width is less than 576px.
   * Updates the `isMobile` flag accordingly.
   */
  private checkScreenSize(): void {
    this.isMobile = window.innerWidth < 576;
  }
  /**
   * Lifecycle hook that is called once just before the component is destroyed.
   * Use this to clean up resources such as subscriptions.
   */
  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
